import { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { IconAnexo, IconLixoDesk } from "@/assets/images/Icons";
import { serviceAreaVendas } from "@/services/serviceAreaVendas";
import { mesesPipeline } from "../utils/Utils";
import notify from "@/services/notify.tsx";
import { useQueryClient } from "@tanstack/react-query";
import Select from "react-select";
import debounce from "lodash.debounce";
import CadastroAgenciaService from "@/services/cadastroAgencia";
import GetAllClientes from "@/services/getClientes";
import Cleave from "cleave.js/react";
import VMasker from "vanilla-masker";
import { ModalBase } from "../ModalBase/ModalBase";
import { normalizeCampaignLinks } from "@/pages/UserGoOn/Campanhas/utils";

interface Props {
  campanha?: any;
  isOpen: boolean;
  setIsOpen: any;
}

export function ModalCriacaoCampanha({ campanha, isOpen, setIsOpen }: Props) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { register, handleSubmit, setValue, reset, watch, control } = useForm();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [agencys, setAgencys] = useState<any>([]);
  const [loadingAgency, setLoadingAgency] = useState<boolean>(false);
  const [clientes, setClientes] = useState<any>([]);
  const [loadingClientes, setLoadingClientes] = useState<boolean>(false);
  const {
    fields: linkFields,
    append: appendLink,
    remove: removeLink,
    replace: replaceLinks,
  } = useFieldArray({
    control,
    name: "links",
  });

  const statusOptions = useMemo(
    () => [
      {
        value: 1,
        label: t("campaigns.status.active"),
      },
      {
        value: 2,
        label: t("campaigns.status.competition"),
      },
      {
        value: 3,
        label: t("campaigns.status.finished"),
      },
    ],
    [t],
  );

  const flowOptions = useMemo(
    () => [
      {
        value: 1,
        label: t("campaigns.flow.agency_planning"),
      },
      {
        value: 2,
        label: t("campaigns.flow.client_approval"),
      },
      {
        value: 3,
        label: t("campaigns.flow.secom_approval"),
      },
      {
        value: 4,
        label: t("campaigns.flow.airing"),
      },
    ],
    [t],
  );

  useEffect(() => {
    if (isOpen) {
      if (campanha) {
        setIsEdit(true);
        const status = statusOptions.find(
          (option) => option.value == campanha?.id_status,
        );

        const mes = mesesPipeline(t).find(
          (option: any) => option.value == campanha?.mes,
        );

        setValue("nome", campanha?.nome);
        const valorFormatado = campanha?.valor
          ? `R$ ${parseFloat(campanha.valor).toLocaleString("pt-BR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`
          : "R$ 0,00";

        setValue("valor", valorFormatado);
        setValue("description", campanha?.description);
        setValue("mes", {
          value: mes?.value,
          label: mes?.label,
        });
        setValue("id_status", {
          value: campanha?.id_status,
          label: status?.label,
        });
        setValue("agency_id", {
          value: campanha?.agency_id,
          label: campanha?.nomeAgencia,
        });
        setValue("id_client", {
          value: campanha?.id_client,
          label: campanha?.nameCliente,
        });
        setValue("id_fluxo", {
          value: campanha?.id_fluxo,
          label:
            flowOptions.find((option) => option.value == campanha?.id_fluxo)
              ?.label ?? campanha?.nameFluxo,
        });
        setValue("ano", campanha?.ano);
        setValue("cidade", campanha?.cidade);
        replaceLinks(normalizeCampaignLinks(campanha?.links));
      } else {
        setIsEdit(false);
        reset();
        replaceLinks([]);
      }
    }
  }, [
    isOpen,
    campanha,
    t,
    setValue,
    reset,
    replaceLinks,
    statusOptions,
    flowOptions,
  ]);

  const adicionarLink = () => {
    appendLink({ link: "", nome: "" });
  };

  const getResponseMessage = (response: any, fallback: string) =>
    response?.data?.msg ||
    response?.data?.message ||
    response?.data?.error ||
    response?.error ||
    fallback;

  const isSuccessResponse = (response: any) =>
    response?.status >= 200 && response?.status < 300;

  const resolveLoadingToast = (
    toastId: any,
    type: "success" | "error",
    message: string,
  ) => {
    notify.dismiss(toastId);
    if (type === "success") {
      notify.success(message);
      return;
    }
    notify.error(message);
  };

  const onSubmit = async (data: any) => {
    setIsSubmitting(true);
    const toastId = notify.loading("Salvando campanha...");
    try {
      data.agency_id = data?.agency_id?.value;
      data.id_client = data?.id_client?.value;
      data.id_fluxo = data?.id_fluxo?.value;
      data.id_status = data?.id_status?.value;
      data.mes = data?.mes?.value;
      data.ano = Number(data?.ano);
      const normalizedLinks = normalizeCampaignLinks(data.links);
      if (normalizedLinks.length > 0 || isEdit) {
        data.links = JSON.stringify(normalizedLinks);
      } else {
        delete data.links;
      }
      if (data.valor) {
        data.valor = data.valor
          .replace(/\./g, "")
          .replace(",", ".")
          .replace("R$ ", "");
      }

      let response;
      const successMessage = isEdit
        ? t("campaigns.messages.edit_success", {
            defaultValue: "Campanha atualizada!",
          })
        : t("campaigns.messages.create_success", {
            defaultValue: "Campanha criada!",
          });

      if (isEdit) {
        response = await serviceAreaVendas.editCampanhas(data, campanha?.id);
      } else {
        response = await serviceAreaVendas.addCampanhas(data);
      }

      if (isSuccessResponse(response)) {
        resolveLoadingToast(toastId, "success", successMessage);
        await queryClient.invalidateQueries({ queryKey: ["campanhas"] });
        onClose();
        return;
      }

      resolveLoadingToast(
        toastId,
        "error",
        getResponseMessage(response, t("campaigns.errors.insert_campaign")),
      );
    } catch (err: any) {
      resolveLoadingToast(
        toastId,
        "error",
        getResponseMessage(err?.response ?? err, t("campaigns.errors.insert_campaign")),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  function onClose() {
    setIsEdit(false);
    reset();
    replaceLinks([]);
    setIsOpen(false);
  }

  const loadAgencys = useCallback(
    debounce(async (inputValue) => {
      if (inputValue) {
        setLoadingAgency(true);
        const res = await CadastroAgenciaService.listarAgencys(
          1,
          10,
          inputValue,
        );
        if (res.status === 200) {
          setAgencys(res.data.data);
        }
        setLoadingAgency(false);
      } else {
        setAgencys([]);
      }
    }, 300),
    [],
  );

  const handleInputChangeAgency = (inputValue: any) => {
    loadAgencys(inputValue);
    return inputValue;
  };

  const loadClientes = useCallback(
    debounce(async (inputValue) => {
      if (inputValue) {
        setLoadingClientes(true);
        const res = await GetAllClientes.listClientesPagination(
          1,
          10,
          inputValue,
        );
        if (res.status === 200) {
          setClientes(res.data.data);
        }
        setLoadingClientes(false);
      } else {
        setClientes([]);
      }
    }, 300),
    [],
  );

  const handleInputChangeClientes = (inputValue: any) => {
    loadClientes(inputValue);
    return inputValue;
  };

  return (
    <>
      <ModalBase
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) {
            reset();
            replaceLinks([]);
            setIsOpen(false);
            setIsEdit(false);
          }
        }}
        className="bg-white rounded-lg pr-1 py-1 max-w-3xl w-full max-h-[90vh]"
        title="Criação de campanha"
      >
        <div className="w-full overflow-y-auto max-h-full relative p-6">
          <h2 className="text-2xl font-medium mb-2">
            {isEdit ? t("campaigns.edit") : t("campaigns.register")}{" "}
            {t("campaigns.campaign")}
          </h2>
          <form
            className="grid grid-cols-2 gap-3 w-full"
            onSubmit={handleSubmit(onSubmit)}
          >
            <label className="flex flex-col gap-1 text-cinza-300 col-span-2">
              <span>{t("campaigns.fields.campaign_name")}*</span>
              <input
                className="bg-cinza-100 text-center py-2 rounded-md text-cinza-800 w-full"
                {...register("nome")}
                type="text"
                placeholder={t("campaigns.fields.campaign_name")}
                required
              />
            </label>
            <label className="flex flex-col gap-1 text-cinza-300">
              <span>{t("campaigns.fields.month")}*</span>
              <Select
                {...register("mes")}
                required
                placeholder={t("campaigns.fields.month")}
                className=""
                value={watch("mes")}
                onChange={(value: any) => {
                  setValue("mes", value);
                }}
                options={mesesPipeline(t).map((e: any) => ({
                  value: e.value,
                  label: e.label,
                }))}
                noOptionsMessage={() => t("common:type_to_search")}
              />
            </label>
            <label className="flex flex-col gap-1 text-cinza-300 ">
              <span>{t("campaigns.fields.year")}*</span>
              <input
                required
                {...register("ano")}
                onChange={(e) => {
                  setValue("ano", VMasker.toPattern(e.target?.value, "9999"));
                }}
                placeholder={t("campaigns.fields.year")}
                className="bg-white rounded-[10px] text-[#3d3d3d] border border-[#cccccc] min-h-[38px] px-2 placeholder:text-[#808080] duration-100 delay-0 focus:outline-none focus:ring-2 focus:ring-[#2684FF] focus:rounded-[4px] focus:border-transparent"
              />
            </label>
            <label className="flex flex-col gap-1 text-cinza-300">
              <span>{t("campaigns.fields.agency")}*</span>
              <Select
                {...register("agency_id")}
                required
                placeholder={t("campaigns.fields.agency")}
                className=""
                onInputChange={handleInputChangeAgency}
                isLoading={loadingAgency}
                value={watch("agency_id")}
                onChange={(value: any) => {
                  setValue("agency_id", value);
                }}
                options={[
                  ...agencys.map((e: any) => ({
                    value: e.id,
                    label: e.name_fantasy,
                  })),
                ]}
                noOptionsMessage={() => t("common:type_to_search")}
              />
            </label>
            <label className="flex flex-col gap-1 text-cinza-300">
              <span>{t("campaigns.fields.client")}*</span>
              <Select
                {...register("id_client")}
                required
                placeholder={t("campaigns.fields.client")}
                onInputChange={handleInputChangeClientes}
                isLoading={loadingClientes}
                value={watch("id_client")}
                onChange={(value: any) => {
                  setValue("id_client", value);
                }}
                options={[
                  ...clientes.map((e: any) => ({
                    value: e.id,
                    label: !!e?.name_fantasy ? e?.name_fantasy : e?.nome_completo,
                  })),
                ]}
                noOptionsMessage={() => t("common:type_to_search")}
              />
            </label>
            <label className="flex flex-col gap-1 text-cinza-300 ">
              <span>{t("campaigns.fields.budget")}*</span>
              <Controller
                name="valor"
                control={control}
                render={({ field }) => (
                  <Cleave
                    {...field}
                    options={{
                      numeral: true,
                      numeralThousandsGroupStyle: "thousand",
                      numeralDecimalMark: ",",
                      delimiter: ".",
                      prefix: "R$ ",
                    }}
                    required
                    placeholder="R$ 1000,00"
                    className="bg-cinza-100 rounded-lg py-2 px-5"
                  />
                )}
              />
            </label>
            <label className="flex flex-col gap-1 text-cinza-300">
              <span>{t("campaigns.fields.status")}*</span>
              <Select
                {...register("id_status")}
                placeholder={t("campaigns.fields.status")}
                className=""
                onInputChange={handleInputChangeClientes}
                value={watch("id_status")}
                onChange={(value: any) => {
                  setValue("id_status", value);
                }}
                required
                options={statusOptions}
              />
            </label>
            <label className="flex flex-col gap-1 text-cinza-300">
              <span>{t("campaigns.fields.flow")}*</span>
              <Select
                {...register("id_fluxo")}
                placeholder={t("campaigns.fields.flow")}
                className=""
                onInputChange={handleInputChangeClientes}
                value={watch("id_fluxo")}
                onChange={(value: any) => {
                  setValue("id_fluxo", value);
                }}
                required
                options={flowOptions}
              />
            </label>
            <label className="flex flex-col gap-1 text-cinza-300 ">
              <span>{t("campaigns.fields.city")}*</span>
              <input
                required
                {...register("cidade")}
                onChange={(e) => {
                  setValue("cidade", e.target?.value);
                }}
                placeholder={t("campaigns.fields.city")}
                className="bg-white rounded-[10px] text-[#3d3d3d] border border-[#cccccc] min-h-[38px] px-2 placeholder:text-[#808080] duration-100 delay-0 focus:outline-none focus:ring-2 focus:ring-[#2684FF] focus:rounded-[4px] focus:border-transparent"
              />
            </label>
            <div className="space-y-2 col-span-2">
              <div className="flex items-center gap-3 font-medium text-cinza-300">
                <IconAnexo size={24} />
                <span className="text-sm uppercase">
                  {t("campaigns.links.title")}
                </span>
                <button
                  type="button"
                  onClick={adicionarLink}
                  className="text-azul-400 underline text-sm"
                >
                  {t("campaigns.links.add")}
                </button>
              </div>

              {linkFields.map((field, index) => (
                <div key={field.id} className="flex items-center gap-3">
                  <input
                    {...register(`links.${index}.link`)}
                    className="rounded-md bg-cinza-100 px-4 py-2 w-full"
                    type="text"
                    placeholder={t("campaigns.links.linkPlaceholder")}
                    required
                  />
                  <input
                    {...register(`links.${index}.nome`)}
                    className="rounded-md bg-cinza-100 px-4 py-2 w-full"
                    type="text"
                    placeholder={t("campaigns.links.namePlaceholder")}
                  />
                  <button
                    type="button"
                    title={t("campaigns.links.remove")}
                    onClick={() => removeLink(index)}
                    className="flex items-center justify-center text-red-500"
                  >
                    <IconLixoDesk />
                  </button>
                </div>
              ))}
            </div>
            <label className="flex flex-col gap-1 text-cinza-300 col-span-2">
              <span>{t("campaigns.fields.description")}</span>
              <textarea
                placeholder={t("campaigns.fields.description")}
                {...register("description")}
                className="bg-cinza-100 text-center py-2 rounded-md text-cinza-800 resize-none h-24 w-full"
              />
            </label>

            <button className="bg-azul-400 text-white py-2 rounded-md w-full col-span-2">
              {isSubmitting ? t("common:saving") : t("common:save")}
            </button>
          </form>
        </div>

      </ModalBase>
    </>
  );
}
